import axios from "axios";
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { addAlert } from '../../store/slices/alertReducer'
import { Icon } from '@a-ui/react';
import Spinner from '../Spinner/Spinner';
import { createTaskAttachment, fetchTaskAttachments, setAttachmentLoading, resetAttachmentLoading } from '../../store/slices/tasksReducer'

const FileUpload = (props) => {
  const dispatch = useDispatch();
  const { task, attachmentLoading } = useSelector(state => state.tasks);
  const { user } = useSelector(state => state.user);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState();
  const [hresponse, sethresponse] = useState();
  const [error, setError] = useState({});
  const uploadQueueRef = useRef([]);
  const isUploadingRef = useRef(false);
  
  // Genereer een nieuwe hCaptcha token
  const generateHcaptchaToken = () => {
    return new Promise((resolve, reject) => {
      if (window.hcaptcha) {
        // Reset eerst de vorige challenge
        window.hcaptcha.reset();
        // Genereer een nieuwe token
        window.hcaptcha.execute({ async: true })
          .then(({ response, key }) => {
            resolve(response);
          })
          .catch(err => {
            console.error('hCaptcha error:', err);
            reject(new Error('hCaptcha token generatie mislukt'));
          });
      } else {
        // hCaptcha not available - this should not happen in production
        console.error('hCaptcha not available');
        reject(new Error('hCaptcha is niet beschikbaar'));
      }
    });
  };

  useEffect(() => {
    // Genereer initiële token bij mount
    generateHcaptchaToken()
      .then((token) => {
        sethresponse(token);
      })
      .catch((err) => {
        console.error('Failed to generate initial hCaptcha token:', err);
        // Don't set empty token - let upload fail if hCaptcha is required
      });
  }, []);

  const submit = async (fileitem) => {
    // Genereer een nieuwe hCaptcha token voor elke upload
    let token;
    try {
      token = await generateHcaptchaToken();
    } catch (err) {
      setError(prev => ({ ...prev, [fileitem.name]: 'hCaptcha token kon niet worden gegenereerd. Probeer het opnieuw.' }));
      setFiles(prevFiles => prevFiles.filter(f => f !== fileitem.name));
      dispatch(resetAttachmentLoading());
      return { success: false };
    }
    
    const formData = new FormData();
    formData.append("file", fileitem || file);
    formData.append('userId', user.userName);
    formData.append('generateThumbnail', 'true');
    dispatch(setAttachmentLoading());
    
    try {
      const response = await axios.post(
        `/api/upload`,
        formData,
        { headers: { hresponse: token } }
      );
      
      const mediaInfo = {
        'fileName': fileitem.name || file.name,
        'downloadUrl': response.data.links[0].href,
        'thumbnailURL': response.data.links[1] ? response.data.links[1].href : ''
      };
      
      await dispatch(createTaskAttachment({
        taskId: task.id,
        assetId: JSON.stringify(mediaInfo)
      }));
      
      // Verwijder dit bestand uit de lijst
      setFiles(prevFiles => prevFiles.filter(f => f !== fileitem.name));
      dispatch(fetchTaskAttachments(task.id));
      
      // Genereer een nieuwe token voor de volgende upload
      const newToken = await generateHcaptchaToken();
      sethresponse(newToken);
      
      return { success: true };
    } catch (e) {
      // Toon specifieke foutboodschap van de backend (bv. bestand > 3MB na compressie)
      const backendMessage = e?.response?.data?.message;
      if (backendMessage) {
        setError(prev => ({ ...prev, [fileitem.name]: backendMessage }));
      } else {
        dispatch(addAlert(e));
      }
      
      // Verwijder dit bestand uit de lijst bij error
      setFiles(prevFiles => prevFiles.filter(f => f !== fileitem.name));
      dispatch(fetchTaskAttachments(task.id));
      
      // Genereer een nieuwe token voor de volgende upload, ook bij errors
      const newToken = await generateHcaptchaToken();
      sethresponse(newToken);
      
      return { success: false };
    }
  };

  const processUploadQueue = async () => {
    if (isUploadingRef.current || uploadQueueRef.current.length === 0) {
      return;
    }
    
    isUploadingRef.current = true;
    const nextFile = uploadQueueRef.current.shift();
    
    await submit(nextFile);
    
    isUploadingRef.current = false;
    
    // Als er nog bestanden in de queue zijn, verwerk de volgende
    if (uploadQueueRef.current.length > 0) {
      setTimeout(() => processUploadQueue(), 100);
    } else {
      dispatch(resetAttachmentLoading());
    }
  };
  return (<>
    <div className="m-upload u-margin-bottom">
      <div className="m-upload__inner">
        <div className="m-upload__dropzone">
          <input
            id="upload"
            type="file"
            multiple="multiple"
            className="m-upload__input"
            onChange={(e) => {
              setError({});
              const fileList = Array.from(e.target.files);
              const fileNames = fileList.map(f => f.name);
              
              // Voeg bestanden toe aan de lijst
              setFiles(prevFiles => [...prevFiles, ...fileNames]);
              
              // Voeg bestanden toe aan de upload queue
              uploadQueueRef.current = [...uploadQueueRef.current, ...fileList];
              
              // Start verwerking van de queue
              if (!isUploadingRef.current) {
                processUploadQueue();
              }
              
              // Reset input zodat dezelfde bestanden opnieuw kunnen worden geselecteerd
              e.target.value = '';
            }}
          />
          <div className="m-upload__content">
            <label htmlFor="upload" className="m-upload__message">
              { attachmentLoading === "loading" && <Spinner className="u-margin-right-xxs">loading...</Spinner> } Klik om een bestand toe te voegen</label>
          </div>
        </div>
      </div>
      <small className="m-upload__description">Max. 3MB. Grotere afbeeldingen worden indien mogelijk automatisch verkleind.</small>
      <ul className="m-upload__files u-margin-bottom">
        { files.map((file) => {
          return (<li key={file} className={error[file] ? "is-error" : ""}>
            <Icon name="ai-common-file-empty" className="u-margin-right-xxs"/>
            <span className="m-upload__filename">{file}</span>
            { error[file] && <span className="m-upload__error">{error[file]}</span> }
            <button
              className="m-upload__delete a-button a-button--text a-button--neutral a-button--s has-icon"
              onClick={(e) => {
                setFiles(files.filter((f) => (f !== file)));
                setError(prev => {
                  const newError = { ...prev };
                  delete newError[file];
                  return newError;
                });
                // Verwijder ook uit queue als het daar nog in staat
                uploadQueueRef.current = uploadQueueRef.current.filter(f => f.name !== file);
              }}
            >
              <Icon name="ai-remove" className="u-margin-right-xxs"/>
              <span className="u-screen-reader-only">Close</span>
            </button>
          </li>);
        })}
      </ul>
    </div>
  </>);
};

export default FileUpload;
